import { useMemo } from 'react';
import { useAuth } from '@clerk/react';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useDiffitUserContext } from '../../../contexts/DiffitUserContext';
import { useSignInModal } from '../../../contexts/SignInModalContext';
import { shouldGateSignIn } from './signInGate';

/**
 * Auth gates for the Unlimited workshop. Both guards take a callback and either run it
 * or open the sign-in modal instead, replacing the repeated
 * `if (isLoaded && !isSignedIn) { showSignInModal(); return; }` pattern.
 *
 * Returned as a pair from one call so the two only differ by the bypass, and so the
 * provider subscribes to Clerk / LD / the sign-in modal once.
 *
 * @returns {{requireAuth: Function, requireAuthForMenus: Function}}
 *   - `requireAuth` — always gates anonymous users. For account-bound features, e.g.
 *     prompt history, which reads the signed-in user's prompts from an authed endpoint.
 *   - `requireAuthForMenus` — lets anonymous users through once experimentFreeGenerate
 *     is on and LD has identified them (see signInGate.js). For the Attach / Adapt /
 *     Align menus, which only configure a generation those users can already run.
 */
export const useRequireAuth = () => {
  const { isSignedIn, isLoaded } = useAuth();
  const { experimentFreeGenerate } = useFlags();
  const { isLDidentified } = useDiffitUserContext();
  const showSignInModal = useSignInModal();

  return useMemo(() => {
    const guard = (allowAnonymousWhenFreeGenerate) => (callback) => {
      if (
        shouldGateSignIn({
          isLoaded,
          isSignedIn,
          allowAnonymousWhenFreeGenerate,
          isLDidentified,
          experimentFreeGenerate: !!experimentFreeGenerate,
        })
      ) {
        showSignInModal();
        return;
      }
      callback();
    };

    return { requireAuth: guard(false), requireAuthForMenus: guard(true) };
  }, [isLoaded, isSignedIn, isLDidentified, experimentFreeGenerate, showSignInModal]);
};
